import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Map, AdvancedMarker, Pin, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_MAP_ID, TAFT_CENTER, googleMapsDirectionsUrl } from "../components/GoogleMapsProvider";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ServiceDaysStrip from "../components/ServiceDaysStrip";
import Customers from "./Customers";
import {
  Plus, ArrowUp, ArrowDown, Trash, MapPin, Clock, Money, Recycle, Truck, X,
  PencilSimple, ListNumbers, MagnifyingGlass, Crosshair, Flag, FlagCheckered, Warning,
  NavigationArrow, ArrowsOut, ArrowsIn,
} from "@phosphor-icons/react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DEFAULT_CENTER = [TAFT_CENTER.lat, TAFT_CENTER.lng];

// Polyline drawn via google.maps.Polyline imperatively (no <Polyline> in @vis.gl/react-google-maps)
function RoutePolyline({ path }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps || !path || path.length < 2) return;
    const poly = new window.google.maps.Polyline({
      path: path.map(([lat, lng]) => ({ lat, lng })),
      geodesic: true,
      strokeColor: "#012340",
      strokeOpacity: 0,
      icons: [{
        icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3 },
        offset: "0",
        repeat: "12px",
      }],
      map,
    });
    return () => poly.setMap(null);
  }, [map, JSON.stringify(path)]); // eslint-disable-line
  return null;
}

function AutoFitBounds({ points, fallback }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    if (points && points.length) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
      map.fitBounds(bounds, 60);
      // cap maxZoom for single-point bounds
      const listener = window.google.maps.event.addListenerOnce(map, "idle", () => {
        if (map.getZoom() > 15) map.setZoom(15);
      });
      return () => window.google.maps.event.removeListener(listener);
    } else if (fallback) {
      map.setCenter({ lat: fallback[0], lng: fallback[1] });
      map.setZoom(11);
    }
  }, [map, JSON.stringify(points), JSON.stringify(fallback)]); // eslint-disable-line
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !target) return;
    map.panTo({ lat: target[0], lng: target[1] });
    map.setZoom(16);
  }, [map, target ? target[0] : null, target ? target[1] : null]); // eslint-disable-line
  return null;
}

export default function RoutesPage() {
  const { user } = useAuth();
  const isVisitor = user?.role === "visitor";
  const displayAddr = (a) => (isVisitor ? "•••• redacted" : a);
  const [routes, setRoutes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [dayFilter, setDayFilter] = useState("monthly"); // monthly | all | Mon..Sun
  const [search, setSearch] = useState("");
  const [myPos, setMyPos] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [selectedPin, setSelectedPin] = useState(null);
  const [editCustomerId, setEditCustomerId] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);

  const [showRouteForm, setShowRouteForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [routeForm, setRouteForm] = useState({ route_number: "", name: "" });

  async function loadAll() {
    const [r, c, d, s] = await Promise.all([
      api.get("/routes"), api.get("/customers"), api.get("/drivers"), api.get("/settings"),
    ]);
    const sorted = [...r.data].sort((a, b) => String(a.route_number || "").localeCompare(String(b.route_number || ""), undefined, { numeric: true }));
    setRoutes(sorted);
    setCustomers(c.data);
    setDrivers(d.data);
    setSettings(s.data);
    if (!activeId && sorted.length) setActiveId(sorted[0].id);
  }
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const active = useMemo(() => routes.find((r) => r.id === activeId) || null, [routes, activeId]);

  useEffect(() => {
    if (!active) { setAnalytics(null); return; }
    const params = { period: "monthly" };
    if (dayFilter === "monthly") params.period = "monthly";
    else if (dayFilter === "all") params.period = "weekly";
    else { params.period = "daily"; params.day = dayFilter; }
    setAnalyticsLoading(true);
    api.get(`/analytics/route/${active.id}`, { params })
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics(null))
      .finally(() => setAnalyticsLoading(false));
  }, [active?.id, active?.stop_ids?.length, active?.driver_id, dayFilter]);

  // Container-aware lookups: each route.stop_ids entry is a CONTAINER id.
  // We need to map container_id -> { customer, container }.
  const containerMap = useMemo(() => {
    const map = {};
    for (const c of customers) {
      const cts = (c.containers && c.containers.length)
        ? c.containers
        : [{
            id: c.id, // legacy fallback uses customer id
            waste_stream: "MSW",
            container_size: c.dumpster_size,
            quantity: 1,
            service_frequency: c.service_frequency,
            service_days: c.service_days || [],
            monthly_price: c.monthly_price || 0,
            status: c.status || "active",
            driver_notes: "",
          }];
      for (const ct of cts) {
        map[ct.id] = { customer: c, container: ct };
      }
    }
    return map;
  }, [customers]);

  /**
   * Flat list of stops for the active route. Each entry merges customer
   * (for address/coords) with container (for stream/size/days/price).
   */
  const stopsAll = useMemo(() => {
    return (active?.stop_ids || [])
      .map((id) => containerMap[id])
      .filter(Boolean)
      .map(({ customer, container }) => ({
        id: container.id,
        container_id: container.id,
        customer_id: customer.id,
        business_name: customer.business_name,
        // Container-level address wins when set — one customer can have cans
        // at multiple physical locations. Falls back to the customer's main
        // service_address so legacy containers keep behaving.
        service_address: container.service_address || customer.service_address,
        lat: (container.lat != null && container.lng != null) ? container.lat : customer.lat,
        lng: (container.lat != null && container.lng != null) ? container.lng : customer.lng,
        service_notes: customer.service_notes,
        // container-level
        waste_stream: container.waste_stream || "MSW",
        dumpster_size: container.container_size,
        quantity: container.quantity || 1,
        service_days: container.service_days || [],
        service_frequency: container.service_frequency,
        monthly_price: container.monthly_price || 0,
        driver_notes: container.driver_notes || "",
        status: container.status || "active",
      }));
  }, [active, containerMap]);

  const stops = useMemo(() => {
    // "monthly" + "all" both show every stop on the route; daily shows only that weekday
    if (dayFilter === "monthly" || dayFilter === "all") return stopsAll;
    return stopsAll.filter((s) => (s.service_days || []).includes(dayFilter));
  }, [stopsAll, dayFilter]);

  const availableCustomers = useMemo(() => {
    // "Available containers" picker — show every container NOT already on this route.
    const onRoute = new Set(active?.stop_ids || []);
    const t = search.trim().toLowerCase();
    const items = [];
    for (const c of customers) {
      const cts = (c.containers && c.containers.length) ? c.containers : [];
      for (const ct of cts) {
        if (onRoute.has(ct.id)) continue;
        if ((ct.status || "active") === "inactive") continue;
        const label = `${c.business_name} · ${ct.waste_stream} (${ct.container_size}yd³)`;
        if (t && ![c.business_name, c.service_address, ct.service_address, ct.waste_stream].filter(Boolean).join(" ").toLowerCase().includes(t)) continue;
        items.push({
          id: ct.id,
          customer_id: c.id,
          business_name: c.business_name,
          // Same container-first fallback so the available-containers picker
          // shows the correct physical address for each can.
          service_address: ct.service_address || c.service_address,
          waste_stream: ct.waste_stream,
          container_size: ct.container_size,
          service_days: ct.service_days || [],
          label,
        });
      }
    }
    return items;
  }, [customers, active, search]);

  // Stats — pickups per week summed across containers on this route
  const weeklyStops = useMemo(() => {
    return stopsAll.reduce((acc, s) => acc + (s.service_days || []).length, 0);
  }, [stopsAll]);

  function openCreateRoute() {
    setEditingRoute(null);
    setRouteForm({ route_number: "", name: "" });
    setShowRouteForm(true);
  }
  function openEditRoute() {
    if (!active) return;
    setEditingRoute(active);
    setRouteForm({ route_number: active.route_number || "", name: active.name || "" });
    setShowRouteForm(true);
  }
  async function submitRoute(e) {
    e.preventDefault();
    const payload = {
      route_number: routeForm.route_number.trim(),
      name: routeForm.name.trim(),
      driver_id: editingRoute?.driver_id || null,
      stop_ids: editingRoute?.stop_ids || [],
    };
    if (!payload.route_number) { toast.error("Route number required"); return; }
    try {
      let data;
      if (editingRoute) {
        ({ data } = await api.put(`/routes/${editingRoute.id}`, payload));
        toast.success("Route updated");
      } else {
        ({ data } = await api.post("/routes", payload));
        toast.success("Route created");
      }
      setShowRouteForm(false);
      await loadAll();
      setActiveId(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  }

  async function deleteRoute() {
    if (!active || !window.confirm(`Delete Route #${active.route_number}? Stops will not be deleted.`)) return;
    await api.delete(`/routes/${active.id}`);
    setActiveId(null);
    await loadAll();
    toast.success("Route deleted");
  }

  async function assignDriver(driver_id) {
    if (!active) return;
    await api.put(`/routes/${active.id}`, { ...activeBody(active), driver_id: driver_id || null });
    await loadAll();
  }
  function activeBody(r) {
    return { route_number: r.route_number, name: r.name || "", driver_id: r.driver_id || null, stop_ids: r.stop_ids || [] };
  }

  async function addStop(cid) {
    if (!active) return;
    const stop_ids = [...(active.stop_ids || []), cid];
    await api.put(`/routes/${active.id}/reorder`, { stop_ids });
    await loadAll();
  }
  async function removeStop(cid) {
    if (!active) return;
    const stop_ids = (active.stop_ids || []).filter((x) => x !== cid);
    await api.put(`/routes/${active.id}/reorder`, { stop_ids });
    await loadAll();
  }
  async function moveStop(realIdx, dir) {
    const arr = [...(active.stop_ids || [])];
    const j = realIdx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[realIdx], arr[j]] = [arr[j], arr[realIdx]];
    await api.put(`/routes/${active.id}/reorder`, { stop_ids: arr });
    await loadAll();
  }

  async function onDragEnd(result) {
    // Visitors are read-only — no PUT allowed. Guarding here as a belt-and-
    // suspenders on top of the hidden drag handle so a stray drag from a
    // browser accessibility tool never explodes into a 403 error overlay.
    if (isVisitor) return;
    if (!result.destination || !active) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;
    // The dnd reorders the *filtered* `stops` array (the visible one). To preserve
    // hidden (non-day-matching) stops, we splice the real stop_ids list.
    const filteredIds = stops.map((s) => s.id);
    const movedId = filteredIds[src];
    const newFiltered = [...filteredIds];
    newFiltered.splice(src, 1);
    newFiltered.splice(dst, 0, movedId);
    // Rebuild full stop_ids by walking the original list, replacing slots from `newFiltered` in order
    const visibleSet = new Set(filteredIds);
    const out = [];
    let f = 0;
    for (const id of (active.stop_ids || [])) {
      if (visibleSet.has(id)) {
        out.push(newFiltered[f++]);
      } else {
        out.push(id);
      }
    }
    // Optimistic UI
    setRoutes((rs) => rs.map((r) => (r.id === active.id ? { ...r, stop_ids: out } : r)));
    try {
      await api.put(`/routes/${active.id}/reorder`, { stop_ids: out });
      await loadAll();
    } catch {
      toast.error("Reorder failed");
      await loadAll();
    }
  }

  const visibleIds = new Set(stops.map((s) => s.id));
  const mappableStopsAll = stopsAll.filter((s) => s.lat && s.lng);
  const mappableVisible = stops.filter((s) => s.lat && s.lng);
  const missingPins = stopsAll.length - mappableStopsAll.length;

  const yardPt = settings?.yard_lat && settings?.yard_lng ? [settings.yard_lat, settings.yard_lng] : null;
  const endPt = settings?.end_lat && settings?.end_lng ? [settings.end_lat, settings.end_lng] : null;
  const fallback = [
    settings?.default_center_lat ?? DEFAULT_CENTER[0],
    settings?.default_center_lng ?? DEFAULT_CENTER[1],
  ];

  // fit bounds: visible stops + yard + end (so the route always frames properly)
  const fitPoints = [...mappableVisible.map((s) => [s.lat, s.lng])];
  if (yardPt) fitPoints.push(yardPt);
  if (endPt) fitPoints.push(endPt);
  if (myPos) fitPoints.push(myPos);

  // polyline: yard → stops → end
  const polylinePts = [];
  if (yardPt) polylinePts.push(yardPt);
  mappableVisible.forEach((s) => polylinePts.push([s.lat, s.lng]));
  if (endPt) polylinePts.push(endPt);

  function useMyLocation() {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setMyPos(p);
        setFlyTarget(p);
        toast.success("Centered on your current location");
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          toast.error("Location permission was denied. Please allow location access in your browser settings.", { duration: 6000 });
        } else {
          toast.error(err.message || "Could not read your location");
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  return (
    <div className="space-y-5" data-testid="routes-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#5C738A]">Operations</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-[#012340] mt-1">Route Profiles</h1>
          <p className="text-sm text-[#5C738A] mt-1">Routes organized by number — each spans the whole week.</p>
        </div>
        {!isVisitor && (
          <button onClick={openCreateRoute} className="btn-primary" data-testid="route-add-btn">
            <Plus size={16} weight="bold" className="mr-1.5" /> Add Route
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Route cards (left) */}
        <div className="lg:col-span-3 space-y-2">
          {routes.map((r) => {
            const stopCount = r.stop_ids?.length || 0;
            const weekly = (r.stop_ids || []).reduce(
              (acc, id) => acc + ((containerMap[id]?.container?.service_days || []).length),
              0,
            );
            const driver = drivers.find((d) => d.id === r.driver_id);
            const isActive = active?.id === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setActiveId(r.id)}
                data-testid={`route-card-${r.id}`}
                className={`w-full text-left bg-white border rounded-sm px-4 py-3 transition-all hover:bg-[#FAFAFA] ${isActive ? "border-[#CFAB7A] border-l-4" : "border-[#012340]/10"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <ListNumbers size={16} weight="bold" className="text-[#012340]/60 flex-shrink-0" />
                    <div className="font-heading font-black text-[#012340]">#{r.route_number}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A] font-bold">{stopCount} stops</span>
                </div>
                {r.name && <div className="text-xs text-[#5C738A] mt-1 truncate">{r.name}</div>}
                <div className="flex items-center justify-between mt-1.5 text-[11px]">
                  <span className="text-[#012340]">{driver?.name || "— Unassigned"}</span>
                  <span className="text-[#5C738A]">{weekly} lifts/wk</span>
                </div>
              </button>
            );
          })}
          {!routes.length && (
            <div className="bg-white border border-dashed border-[#012340]/20 rounded-sm px-4 py-6 text-center text-xs text-[#5C738A]">
              No routes yet. Click <span className="font-bold">Add Route</span>.
            </div>
          )}
        </div>

        {/* Active route detail */}
        <div className="lg:col-span-9 space-y-4">
          {active ? (
            <>
              {/* Header */}
              <div className="bg-white border border-[#012340]/10 rounded-sm p-4 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Route Profile</div>
                  <div className="font-heading text-2xl font-black text-[#012340] flex items-center gap-2">
                    <span>#{active.route_number}</span>
                    {active.name && <span className="text-base font-bold text-[#5C738A] tracking-normal">· {active.name}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {isVisitor ? (
                    <>
                      {active.driver_id && (
                        <span className="text-xs text-[#5C738A]">Driver: <b className="text-[#012340]">{drivers.find((d) => d.id === active.driver_id)?.name || "—"}</b></span>
                      )}
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#CFAB7A]/15 text-[#CFAB7A] text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="route-visitor-lock">
                        View only
                      </span>
                    </>
                  ) : (
                    <>
                      <select
                        value={active.driver_id || ""}
                        onChange={(e) => assignDriver(e.target.value)}
                        data-testid="route-driver-select"
                        className="rounded-sm bg-white border border-[#012340]/20 px-3 py-2 text-sm"
                      >
                        <option value="">— Unassigned —</option>
                        {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                      <button onClick={openEditRoute} className="px-3 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold text-[#012340] inline-flex items-center gap-1.5 hover:bg-[#012340]/5" data-testid="route-edit-btn">
                        <PencilSimple size={14} weight="bold" /> Edit
                      </button>
                      <button onClick={deleteRoute} className="px-3 py-2 rounded-sm border border-[#D32F2F]/30 text-[#D32F2F] text-sm font-bold inline-flex items-center gap-1.5" data-testid="route-delete-btn">
                        <Trash size={14} weight="bold" /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Period filter chips */}
              <div className="bg-white border border-[#012340]/10 rounded-sm p-3 flex flex-wrap items-center gap-2" data-testid="route-period-tabs">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A] mr-1">Period</div>
                <button onClick={() => setDayFilter("monthly")} data-testid="period-monthly" className={`px-3 py-1.5 rounded-sm text-xs font-bold ${dayFilter === "monthly" ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340] hover:bg-[#012340]/5"}`}>Monthly</button>
                <button onClick={() => setDayFilter("all")} data-testid="period-week" className={`px-3 py-1.5 rounded-sm text-xs font-bold ${dayFilter === "all" ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340] hover:bg-[#012340]/5"}`}>All week</button>
                {DAYS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDayFilter(d)}
                    data-testid={`period-${d}`}
                    className={`px-3 py-1.5 rounded-sm text-xs font-bold ${dayFilter === d ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#012340] hover:bg-[#012340]/5"}`}
                  >
                    {d}
                  </button>
                ))}
                <div className="ml-auto text-xs text-[#5C738A]">
                  Showing <span className="font-bold text-[#012340]">{stops.length}</span> / {stopsAll.length} stops
                </div>
              </div>

              {/* Period-aware analytics */}
              <RouteAnalyticsPanel analytics={analytics} loading={analyticsLoading} period={dayFilter} />


              {/* Map */}
              <div className={mapExpanded ? "fixed inset-0 !mt-0 z-[45] bg-white flex flex-col" : "bg-white border border-[#012340]/10 rounded-sm overflow-hidden"} data-testid="route-map-card">
                <div className="px-4 py-3 border-b border-[#012340]/10 flex flex-wrap items-center justify-between gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">
                  <span>Map · {dayFilter === "all" ? "Whole week" : dayFilter}</span>
                  <div className="flex items-center gap-3">
                    {missingPins > 0 && (
                      <span className="inline-flex items-center gap-1 text-[#F57C00] normal-case tracking-normal">
                        <Warning size={12} weight="bold" /> {missingPins} missing pin{missingPins > 1 ? "s" : ""}
                      </span>
                    )}
                    <span>{mappableVisible.length} pinned</span>
                    <button
                      onClick={useMyLocation}
                      data-testid="use-my-location"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-[#012340]/15 bg-white text-[#012340] text-[10px] tracking-[0.2em] hover:bg-[#012340]/5"
                    >
                      <Crosshair size={12} weight="bold" /> My Location
                    </button>
                    <button
                      onClick={() => setMapExpanded((v) => !v)}
                      data-testid="toggle-map-expand"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-[#012340]/15 bg-white text-[#012340] text-[10px] tracking-[0.2em] hover:bg-[#012340]/5"
                    >
                      {mapExpanded ? <><ArrowsIn size={12} weight="bold" /> Exit Full Screen</> : <><ArrowsOut size={12} weight="bold" /> Full Screen</>}
                    </button>
                  </div>
                </div>
                <div style={mapExpanded ? { flex: 1, minHeight: 0 } : { height: "clamp(560px, 75vh, 900px)" }} data-testid="route-map">
                  <Map
                    defaultCenter={{ lat: fallback[0], lng: fallback[1] }}
                    defaultZoom={11}
                    mapId={GOOGLE_MAPS_MAP_ID}
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                    style={{ height: "100%", width: "100%" }}
                  >
                    {polylinePts.length > 1 && <RoutePolyline path={polylinePts} />}
                    {yardPt && (
                      <AdvancedMarker
                        position={{ lat: yardPt[0], lng: yardPt[1] }}
                        onClick={() => setSelectedPin({ id: "__start__", lat: yardPt[0], lng: yardPt[1] })}
                      >
                        <div style={{
                          background: "#2E7D32", color: "#fff", padding: "4px 8px",
                          borderRadius: 4, fontWeight: 800, fontSize: 11, border: "2px solid #fff",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.3)", letterSpacing: "0.1em",
                          fontFamily: "Chivo, sans-serif",
                        }}>START</div>
                      </AdvancedMarker>
                    )}
                    {endPt && (!yardPt || endPt[0] !== yardPt[0] || endPt[1] !== yardPt[1]) && (
                      <AdvancedMarker
                        position={{ lat: endPt[0], lng: endPt[1] }}
                        onClick={() => setSelectedPin({ id: "__end__", lat: endPt[0], lng: endPt[1] })}
                      >
                        <div style={{
                          background: "#D32F2F", color: "#fff", padding: "4px 8px",
                          borderRadius: 4, fontWeight: 800, fontSize: 11, border: "2px solid #fff",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.3)", letterSpacing: "0.1em",
                          fontFamily: "Chivo, sans-serif",
                        }}>END</div>
                      </AdvancedMarker>
                    )}
                    {mappableStopsAll.map((s) => {
                      const idx = stopsAll.findIndex((x) => x.id === s.id) + 1;
                      const dim = !visibleIds.has(s.id);
                      const highlighted = selectedPin?.id === s.id;
                      return (
                        <AdvancedMarker
                          key={s.id}
                          position={{ lat: s.lat, lng: s.lng }}
                          onClick={() => setSelectedPin(s)}
                        >
                          <div style={{
                            background: dim ? "#5C738A" : "#012340",
                            color: "#CFAB7A",
                            width: highlighted ? 38 : 30,
                            height: highlighted ? 38 : 30,
                            borderRadius: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            fontFamily: "Chivo, sans-serif",
                            fontSize: highlighted ? 15 : 13,
                            border: `${highlighted ? 3 : 2}px solid ${highlighted ? "#FFD166" : "#CFAB7A"}`,
                            opacity: dim ? 0.55 : 1,
                            boxShadow: highlighted ? "0 0 0 6px rgba(255,209,102,0.35)" : "0 2px 4px rgba(0,0,0,0.2)",
                            transition: "all 0.2s",
                          }}>{idx}</div>
                        </AdvancedMarker>
                      );
                    })}
                    {myPos && (
                      <AdvancedMarker position={{ lat: myPos[0], lng: myPos[1] }}>
                        <div style={{ position: "relative", width: 32, height: 32 }}>
                          <div style={{
                            position: "absolute", inset: 0, borderRadius: "50%",
                            background: "rgba(207,171,122,0.35)",
                            animation: "rl-pulse 1.6s ease-out infinite",
                          }} />
                          <div style={{
                            position: "absolute", inset: 6, background: "#012340",
                            color: "#CFAB7A", borderRadius: "50%",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            border: "2px solid #CFAB7A", fontWeight: 800, fontSize: 9,
                          }}>ME</div>
                        </div>
                      </AdvancedMarker>
                    )}
                    {selectedPin && selectedPin.id !== "__start__" && selectedPin.id !== "__end__" && (() => {
                      const pin = stopsAll.find((x) => x.id === selectedPin.id) || selectedPin;
                      return (
                      <InfoWindow
                        position={{ lat: pin.lat, lng: pin.lng }}
                        onCloseClick={() => setSelectedPin(null)}
                        pixelOffset={[0, -30]}
                      >
                        <div style={{ minWidth: 200 }} data-testid="pin-popup">
                          <div style={{ fontWeight: 700, color: "#012340" }}>
                            {(stopsAll.findIndex((x) => x.id === pin.id) + 1)}. {pin.business_name}
                          </div>
                          {!isVisitor && (
                            <div style={{ fontSize: 12, color: "#5C738A", margin: "2px 0" }}>{displayAddr(pin.service_address)}</div>
                          )}
                          <div style={{ fontSize: 12 }}>
                            <b>{pin.waste_stream || "MSW"}</b> · <b>{pin.dumpster_size} yd³</b>{pin.quantity > 1 ? ` ×${pin.quantity}` : ""} · Route #{active.route_number}
                          </div>
                          <div style={{ fontSize: 12 }}>Days: {(pin.service_days || []).join(", ") || "—"}</div>
                          {!isVisitor && pin.service_notes && (
                            <div style={{ fontSize: 12, fontStyle: "italic", color: "#5C738A", marginTop: 4 }}>
                              "{pin.service_notes}"
                            </div>
                          )}
                          {!isVisitor && (
                            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                              <button
                                type="button"
                                onClick={() => setEditCustomerId(pin.customer_id)}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "5px 10px", background: "#CFAB7A",
                                  color: "#012340", borderRadius: 4, fontWeight: 700, fontSize: 11,
                                  border: "none", cursor: "pointer",
                                }}
                                data-testid="popup-edit-customer"
                              >
                                <PencilSimple size={12} weight="bold" /> Edit
                              </button>
                              <a
                                href={googleMapsDirectionsUrl({ lat: pin.lat, lng: pin.lng, address: pin.service_address })}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "5px 10px", background: "#012340",
                                  color: "#fff", borderRadius: 4, fontWeight: 700, fontSize: 11,
                                  textDecoration: "none",
                                }}
                                data-testid="popup-open-gmaps"
                              >
                                ↗ Open in Google Maps
                              </a>
                            </div>
                          )}
                        </div>
                      </InfoWindow>
                      );
                    })()}
                    {selectedPin && selectedPin.id === "__start__" && yardPt && (
                      <InfoWindow position={{ lat: yardPt[0], lng: yardPt[1] }} onCloseClick={() => setSelectedPin(null)} pixelOffset={[0, -20]}>
                        <div style={{ minWidth: 180 }}>
                          <div style={{ fontWeight: 700, color: "#012340" }}>{settings?.yard_name || "Start"}</div>
                          <div style={{ fontSize: 12 }}>{settings?.yard_address}</div>
                        </div>
                      </InfoWindow>
                    )}
                    {selectedPin && selectedPin.id === "__end__" && endPt && (
                      <InfoWindow position={{ lat: endPt[0], lng: endPt[1] }} onCloseClick={() => setSelectedPin(null)} pixelOffset={[0, -20]}>
                        <div style={{ minWidth: 180 }}>
                          <div style={{ fontWeight: 700, color: "#012340" }}>{settings?.end_name || "End"}</div>
                          <div style={{ fontSize: 12 }}>{settings?.end_address}</div>
                        </div>
                      </InfoWindow>
                    )}
                    <AutoFitBounds points={fitPoints} fallback={fallback} />
                    <FlyTo target={flyTarget} />
                  </Map>
                </div>
              </div>

              {/* Customers + Available */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-[#012340]/10 rounded-sm">
                  <div className="px-4 py-3 border-b border-[#012340]/10 text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">
                    Customers on this route ({stops.length}{dayFilter !== "all" && ` · ${dayFilter}`})
                  </div>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="route-stops">
                      {(provided) => (
                        <ul
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="divide-y divide-[#012340]/5 max-h-[480px] overflow-y-auto"
                          data-testid="route-stops-scroll"
                        >
                          {stops.map((s, dndIdx) => {
                            const realIdx = (active.stop_ids || []).indexOf(s.id);
                            const num = realIdx + 1;
                            return (
                              <Draggable key={s.id} draggableId={s.id} index={dndIdx}>
                                {(prov, snap) => (
                                  <li
                                    ref={prov.innerRef}
                                    {...prov.draggableProps}
                                    className={`flex items-center gap-3 px-4 py-3 ${snap.isDragging ? "bg-[#CFAB7A]/20 shadow-lg" : ""}`}
                                    data-testid={`stop-${s.id}`}
                                    style={prov.draggableProps.style}
                                  >
                                    <span
                                      {...prov.dragHandleProps}
                                      className={`select-none px-1 py-1 ${isVisitor ? "opacity-0 pointer-events-none" : "cursor-grab active:cursor-grabbing text-[#5C738A] hover:text-[#012340]"}`}
                                      title={isVisitor ? undefined : "Drag to reorder"}
                                      aria-hidden={isVisitor ? "true" : undefined}
                                      data-testid={isVisitor ? undefined : `stop-drag-${s.id}`}
                                    >⋮⋮</span>
                                    <div className="w-7 h-7 rounded-sm bg-[#012340] text-[#CFAB7A] font-heading font-bold grid place-items-center text-sm">{num}</div>
                                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { if (s.lat && s.lng) { setSelectedPin(s); setFlyTarget([s.lat, s.lng]); } }}>
                                      <div className="font-semibold text-sm text-[#012340] truncate flex items-center gap-2">
                                        {s.business_name}
                                        <span className="inline-flex px-1.5 py-0.5 rounded-sm bg-[#CFAB7A]/25 text-[#012340] text-[10px] font-bold">
                                          {s.waste_stream || "MSW"} · {s.dumpster_size}yd³{(s.quantity || 1) > 1 ? ` ×${s.quantity}` : ""}
                                        </span>
                                      </div>
                                      {!isVisitor && s.service_address && (
                                        <div className="text-xs text-[#5C738A] truncate">{displayAddr(s.service_address)}</div>
                                      )}
                                      <div className="mt-1.5 max-w-[200px]">
                                        <ServiceDaysStrip days={s.service_days || []} compact label="" />
                                      </div>
                                    </div>
                                    {s.lat && s.lng && (
                                      <button onClick={() => { setSelectedPin(s); setFlyTarget([s.lat, s.lng]); }} className="p-1.5 rounded-sm hover:bg-[#CFAB7A]/20 text-[#012340]" data-testid={`stop-locate-${s.id}`} title="Locate on map">
                                        <MapPin size={14} weight="bold" />
                                      </button>
                                    )}
                                    {!isVisitor && s.lat && s.lng && (
                                      <a href={googleMapsDirectionsUrl({ lat: s.lat, lng: s.lng, address: s.service_address })} target="_blank" rel="noreferrer" className="p-1.5 rounded-sm hover:bg-[#012340]/5 text-[#012340]" title="Open in Google Maps" data-testid={`stop-nav-${s.id}`}>
                                        <NavigationArrow size={14} weight="bold" />
                                      </a>
                                    )}
                                    {!isVisitor && <button onClick={() => removeStop(s.id)} className="p-1.5 rounded-sm hover:bg-[#D32F2F]/10 text-[#D32F2F]" data-testid={`stop-remove-${s.id}`}><X size={14} weight="bold" /></button>}
                                  </li>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          {!stops.length && (
                            <li className="px-4 py-6 text-center text-xs text-[#5C738A]">
                              {dayFilter === "all" ? "No stops on this route yet. Add from the right →" : `No stops scheduled on ${dayFilter}.`}
                            </li>
                          )}
                        </ul>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>

                {!isVisitor && (
                <div className="bg-white border border-[#012340]/10 rounded-sm">
                  <div className="px-4 py-3 border-b border-[#012340]/10 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Add Containers</span>
                    <div className="relative w-40">
                      <MagnifyingGlass size={12} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-[#5C738A]" />
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search"
                        data-testid="customer-search"
                        className="pl-7 pr-2 py-1.5 w-full rounded-sm bg-white border border-[#012340]/20 text-xs focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
                      />
                    </div>
                  </div>
                  <ul className="divide-y divide-[#012340]/5 max-h-[480px] overflow-y-auto">
                    {availableCustomers.map((c) => (
                      <li key={c.id} className="flex items-center gap-3 px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-[#012340] truncate flex items-center gap-2">
                            {c.business_name}
                            <span className="inline-flex px-1.5 py-0.5 rounded-sm bg-[#CFAB7A]/25 text-[#012340] text-[10px] font-bold">
                              {c.waste_stream} · {c.container_size}yd³
                            </span>
                          </div>
                          <div className="text-xs text-[#5C738A] truncate">{displayAddr(c.service_address)}</div>
                          <div className="mt-1.5 max-w-[200px]">
                            <ServiceDaysStrip days={c.service_days || []} compact label="" />
                          </div>
                        </div>
                        <button onClick={() => addStop(c.id)} className="btn-accent !py-1.5 !px-3 text-xs" data-testid={`add-stop-${c.id}`}>
                          <Plus size={14} weight="bold" className="mr-1" /> Add
                        </button>
                      </li>
                    ))}
                    {!availableCustomers.length && (
                      <li className="px-4 py-6 text-center text-xs text-[#5C738A]">No matching containers.</li>
                    )}
                  </ul>
                </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white border border-[#012340]/10 rounded-sm p-10 text-center">
              <ListNumbers size={32} weight="bold" className="mx-auto text-[#012340]/30" />
              <div className="font-heading text-xl font-bold text-[#012340] mt-3">No route selected</div>
              <p className="text-sm text-[#5C738A] mt-1">Pick a route on the left, or click <span className="font-bold">Add Route</span>.</p>
            </div>
          )}
        </div>
      </div>

      {/* Route form modal */}
      {showRouteForm && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setShowRouteForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitRoute} className="bg-white w-full max-w-md rounded-sm border border-[#012340]/10" data-testid="route-form">
            <div className="px-5 py-4 border-b border-[#012340]/10 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{editingRoute ? "Edit" : "New"}</div>
                <div className="font-heading text-xl font-bold text-[#012340]">Route Profile</div>
              </div>
              <button type="button" onClick={() => setShowRouteForm(false)}><X size={18} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Route Number<span className="text-[#D32F2F]">*</span></label>
                <input
                  required
                  autoFocus
                  className="mt-1 w-full rounded-sm bg-white border border-[#012340]/20 px-3 py-2.5 text-base font-heading font-bold focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
                  placeholder="e.g. 201"
                  value={routeForm.route_number}
                  onChange={(e) => setRouteForm({ ...routeForm, route_number: e.target.value })}
                  data-testid="rf-number"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Name (optional)</label>
                <input
                  className="mt-1 w-full rounded-sm bg-white border border-[#012340]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
                  placeholder="e.g. Downtown Commercial"
                  value={routeForm.name}
                  onChange={(e) => setRouteForm({ ...routeForm, name: e.target.value })}
                  data-testid="rf-name"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#012340]/10 flex justify-end gap-2">
              <button type="button" className="px-4 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold" onClick={() => setShowRouteForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="rf-save">{editingRoute ? "Update" : "Create"}</button>
            </div>
          </form>
        </div>
      )}

      {editCustomerId && (
        <Customers
          key={editCustomerId}
          editOnlyId={editCustomerId}
          onEmbeddedClose={() => setEditCustomerId(null)}
          onEmbeddedSaved={() => loadAll()}
        />
      )}
    </div>
  );
}

function Stat({ label, value, Icon, accent }) {
  return (
    <div className="bg-white border border-[#012340]/10 rounded-sm p-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{label}</div>
        <div className={`w-7 h-7 rounded-sm grid place-items-center ${accent ? "bg-[#CFAB7A] text-[#012340]" : "bg-[#012340] text-[#CFAB7A]"}`}>
          <Icon size={14} weight="bold" />
        </div>
      </div>
      <div className="kpi-number text-2xl mt-2 text-[#012340]">{value}</div>
    </div>
  );
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtNum(n, d = 2) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d });
}
function fmtDateISO(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  } catch { return iso; }
}
function fmtClock(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function fmtHours(h) {
  if (h == null || isNaN(h)) return "—";
  const total = Number(h);
  if (total < 1 / 60) {
    // Sub-minute: show seconds so the live clock visibly moves
    return `${Math.max(0, Math.round(total * 3600))}s`;
  }
  const hh = Math.floor(total);
  const mm = Math.round((total - hh) * 60);
  return `${hh}h ${String(mm).padStart(2, "0")}m`;
}

function periodLabel(period) {
  if (period === "monthly") return "Monthly";
  if (period === "all") return "Weekly";
  return `${period} (Daily)`;
}

function RouteAnalyticsPanel({ analytics, loading, period }) {
  const label = periodLabel(period);
  const m = analytics?.metrics || analytics || {};
  const sessions = analytics?.sessions || [];

  // Build a human-friendly display range. For daily mode, show "Every Mon in <Month> <Year>"
  // instead of the raw calendar-month range (which is used internally for session aggregation).
  function displayRange() {
    if (!analytics?.period_start || !analytics?.period_end) return "—";
    if (analytics.period === "daily" && analytics.day) {
      try {
        const [y, mo] = analytics.period_start.split("-").map(Number);
        const monthLabel = new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
        return `Every ${analytics.day} in ${monthLabel}`;
      } catch { /* fall through */ }
    }
    return `${fmtDateISO(analytics.period_start)} → ${fmtDateISO(analytics.period_end)}`;
  }

  return (
    <div className="space-y-3" data-testid="route-analytics-panel">
      {/* Header strip */}
      <div className="bg-[#012340] text-white border-l-4 border-[#CFAB7A] rounded-sm px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#CFAB7A]">Performance · {label}</div>
          <div className="text-sm font-bold mt-0.5 tabular-nums" data-testid="route-analytics-range">
            {displayRange()}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">
          {loading ? "Loading…" : analytics ? `${m.total_containers || 0} containers · ${m.total_customers || 0} customers` : "—"}
        </div>
      </div>

      {/* Top row — money */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Revenue" value={fmtMoney(m.revenue)} Icon={Money} accent />
        <Stat label="Profit" value={fmtMoney(m.profit)} Icon={Money} accent />
        <Stat label="Disposal Cost" value={fmtMoney(m.estimated_disposal_cost)} Icon={Money} />
        <Stat label="Avg Rev / Yard" value={fmtMoney(m.avg_revenue_per_yard)} Icon={Money} />
      </div>

      {/* Tonnage + yards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Est. Tonnage" value={fmtNum(m.estimated_tonnage, 2)} Icon={Recycle} />
        <Stat label="Yards Serviced" value={fmtNum(m.yards_serviced, 1)} Icon={Recycle} />
        <Stat label="Avg Yards / Stop" value={fmtNum(m.avg_yards_per_stop, 2)} Icon={Recycle} />
        <Stat label="Avg Rev / Stop" value={fmtMoney(m.avg_revenue_per_stop)} Icon={Money} />
      </div>

      {/* Operational counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat
          label={period === "monthly" ? "Weekly Stops" : period === "all" ? "Weekly Stops" : "Stops"}
          value={period === "daily" || period === "all" || period === "monthly" ? fmtNum(m.total_pickups, 0) : fmtNum(m.total_pickups, 0)}
          Icon={Truck}
        />
        <Stat label="Customers" value={fmtNum(m.total_customers, 0)} Icon={MapPin} />
        <Stat label="Containers" value={fmtNum(m.total_containers, 0)} Icon={Truck} />
        <Stat label="Route Hours" value={fmtHours(m.total_route_hours)} Icon={Clock} accent />
        <Stat label="Disposal $/Ton" value={fmtMoney(analytics?.settings?.disposal_cost_per_ton ?? analytics?.tipping_rate)} Icon={Money} />
      </div>

      {/* Route session log */}
      <div className="bg-white border border-[#012340]/10 rounded-sm overflow-hidden" data-testid="route-sessions-card">
        <div className="px-4 py-3 border-b border-[#012340]/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#5C738A]">Route Hours</div>
            <div className="text-sm font-bold text-[#012340]">Driver Time Log</div>
          </div>
          <div className="text-[11px] text-[#5C738A]">
            {sessions.length ? `${sessions.length} session${sessions.length === 1 ? "" : "s"} this ${period === "all" ? "week" : period === "daily" ? "month" : "month"}` : "No sessions"}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A]">
              <tr className="text-left">
                <th className="px-4 py-2">Service Date</th>
                <th className="px-4 py-2">Driver</th>
                <th className="px-4 py-2">Start</th>
                <th className="px-4 py-2">Finish</th>
                <th className="px-4 py-2 text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {sessions.slice(0, 12).map((s) => (
                <tr key={s.id} className="border-t border-[#012340]/5 even:bg-[#FAFAFA]" data-testid={`session-row-${s.id}`}>
                  <td className="px-4 py-2 text-xs text-[#012340] font-mono whitespace-nowrap">{fmtDateISO(s.service_date)}</td>
                  <td className="px-4 py-2 text-xs text-[#012340]">{s.driver_name || "—"}</td>
                  <td className="px-4 py-2 text-xs text-[#5C738A] font-mono whitespace-nowrap">{fmtClock(s.start_time)}</td>
                  <td className="px-4 py-2 text-xs text-[#5C738A] font-mono whitespace-nowrap">{fmtClock(s.finish_time)}</td>
                  <td className="px-4 py-2 text-xs text-[#012340] font-bold text-right tabular-nums">{fmtHours(s.total_hours)}</td>
                </tr>
              ))}
              {!sessions.length && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-xs text-[#5C738A]">Drivers haven't logged any sessions in this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
